/**
 * GFG-194 (Send Queue, GFG Admin) — unit U6. React Query hooks for `/admin/campaigns/send-queue`, consumed
 * by the panel unit (not built here). Key shape and staleTime conventions mirror
 * apps/gfgf-ui/src/hooks/useAdminContacts.ts.
 *
 * Mutations never apply an optimistic update -- every mutation invalidates the `["admin",
 * "send-queue"]` key prefix on success, which refetches the list (the acceptance criterion
 * "after any action the table refreshes"). Errors are left on the mutation result for the panel
 * to render via apiErrorMessage(); nothing here swallows them.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  cancelSend,
  fetchSendQueue,
  fetchSendQueueContactEmail,
  fetchSendQueueContacts,
  fetchSendQueueEmail,
  fetchSendQueueFilterOptions,
  pauseSend,
  removeSendContact,
  rescheduleSend,
  resendSendContactEmail,
  resumeSend,
  sendNow,
  updateSendContactEmail,
} from "@/api/sendQueueCampaigns";
import type {
  RescheduleSendBody,
  SendQueueListParams,
  UpdateSendContactEmailBody,
} from "@/interfaces/sendQueueCampaigns";

const FILTER_OPTIONS_STALE_TIME = 5 * 60 * 1000; // 5 minutes

/** GET /admin/campaigns/send-queue, keyed by every param so distinct filters/sorts/pages cache independently. */
export function useSendQueue(params?: SendQueueListParams) {
  return useQuery({
    queryKey: ["admin", "send-queue", "list", params],
    queryFn: () => fetchSendQueue(params),
  });
}

/** GET /admin/campaigns/send-queue/filter-options -- the account-lead filter dropdown's values. */
export function useSendQueueFilterOptions() {
  return useQuery({
    queryKey: ["admin", "send-queue", "filter-options"],
    queryFn: fetchSendQueueFilterOptions,
    staleTime: FILTER_OPTIONS_STALE_TIME,
  });
}

/** GET /admin/campaigns/send-queue/:id/email -- only fetches while `enabled` (the preview dialog is open). */
export function useSendQueueEmail(id: number, enabled: boolean) {
  return useQuery({
    queryKey: ["admin", "send-queue", "email", id],
    queryFn: () => fetchSendQueueEmail(id),
    enabled: enabled && !!id,
  });
}

/** GET /admin/campaigns/send-queue/:id/contacts -- only fetches while `enabled` (the contacts dialog is open). */
export function useSendQueueContacts(id: number, enabled: boolean) {
  return useQuery({
    queryKey: ["admin", "send-queue", "contacts", id],
    queryFn: () => fetchSendQueueContacts(id),
    enabled: enabled && !!id,
  });
}

/**
 * GET /admin/campaigns/send-queue/:id/contacts/:contactId/email -- the personalised preview for one
 * recipient (the contacts modal's eye icon). Only fetches while `enabled` (the preview is open),
 * same gating as `useSendQueueEmail`.
 */
export function useSendQueueContactEmail(id: number, contactId: number, enabled: boolean) {
  return useQuery({
    queryKey: ["admin", "send-queue", "contact-email", id, contactId],
    queryFn: () => fetchSendQueueContactEmail(id, contactId),
    enabled: enabled && !!id && !!contactId,
  });
}

/**
 * The row-action mutations behind the row menu (pause/resume/send-now/reschedule/cancel) plus the
 * contacts modal's three per-recipient mutations (resend/correct address/remove) -- all eight
 * follow the same rule: no optimistic update, invalidate the `["admin", "send-queue"]` prefix on
 * success.
 */
export function useSendQueueActions() {
  const queryClient = useQueryClient();

  function invalidateSendQueue() {
    // Single prefix invalidates every send-queue query, the list included.
    queryClient.invalidateQueries({ queryKey: ["admin", "send-queue"] });
  }

  const pause = useMutation({
    mutationFn: (id: number) => pauseSend(id),
    onSuccess: invalidateSendQueue,
  });

  const resume = useMutation({
    mutationFn: (id: number) => resumeSend(id),
    onSuccess: invalidateSendQueue,
  });

  const sendNowAction = useMutation({
    mutationFn: (id: number) => sendNow(id),
    onSuccess: invalidateSendQueue,
  });

  const reschedule = useMutation({
    mutationFn: ({ id, body }: { id: number; body: RescheduleSendBody }) =>
      rescheduleSend(id, body),
    onSuccess: invalidateSendQueue,
  });

  const cancel = useMutation({
    mutationFn: (id: number) => cancelSend(id),
    onSuccess: invalidateSendQueue,
  });

  const resendContact = useMutation({
    mutationFn: ({ id, contactId }: { id: number; contactId: number }) =>
      resendSendContactEmail(id, contactId),
    onSuccess: invalidateSendQueue,
  });

  const updateContactEmail = useMutation({
    mutationFn: ({
      id,
      contactId,
      body,
    }: {
      id: number;
      contactId: number;
      body: UpdateSendContactEmailBody;
    }) => updateSendContactEmail(id, contactId, body),
    onSuccess: invalidateSendQueue,
  });

  const removeContact = useMutation({
    mutationFn: ({ id, contactId }: { id: number; contactId: number }) =>
      removeSendContact(id, contactId),
    onSuccess: invalidateSendQueue,
  });

  return {
    pause,
    resume,
    sendNow: sendNowAction,
    reschedule,
    cancel,
    resendContact,
    updateContactEmail,
    removeContact,
  };
}
